import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { CourseResp, CourseStudySignResp } from '@/lib/api';
import { api } from '@/lib/api';
import { getToken } from '@/lib/storage';
import Hls from 'hls.js';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Avatar from '@/components/base/Avatar';

type CoursePeriod = NonNullable<NonNullable<CourseResp['chapterRespList']>[number]['periodRespList']>[number];
type CourseChapter = NonNullable<CourseResp['chapterRespList']>[number];
type RightTab = 'info' | 'comment';

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? `0${secs}` : secs}`;
};

const toPlayUrl = (payload: CourseStudySignResp) => {
  if (payload.vodPlayConfig) {
    try {
      const config = JSON.parse(payload.vodPlayConfig) as { token?: string };
      if (config.token) {
        return `https://customer-8z2cah750k1zb5ci.cloudflarestream.com/${config.token}/manifest/video.m3u8`;
      }
    } catch {
      // Ignore invalid JSON and fallback to direct URLs.
    }
  }
  return payload.resourceUrl || payload.vid || '';
};

const splitKeywords = (value?: string) =>
  (value ?? '')
    .split(/[、,，/|]/)
    .map((item) => item.trim())
    .filter(Boolean);

const CourseStudyPage = () => {
  const { courseId = '' } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerWrapRef = useRef<HTMLDivElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const syncTimerRef = useRef<number | null>(null);
  const maxTimeWatchedRef = useRef(0);

  const [loading, setLoading] = useState(true);
  const [studyLoading, setStudyLoading] = useState(false);
  const [error, setError] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferPercent, setBufferPercent] = useState(0);
  const [course, setCourse] = useState<CourseResp | null>(null);
  const [comments, setComments] = useState<Array<{ id: string | number; commentText: string; nickname: string }>>([]);
  const [activeChapterIdx, setActiveChapterIdx] = useState(0);
  const [activePeriodId, setActivePeriodId] = useState<string>('');
  const [activeType, setActiveType] = useState<'video' | 'image' | 'doc' | ''>('');
  const [docUrl, setDocUrl] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [studyRes, setStudyRes] = useState<CourseStudySignResp | null>(null);
  const [showNextMask, setShowNextMask] = useState(false);
  const [rightTab, setRightTab] = useState<RightTab>('info');

  const chapters = course?.chapterRespList || [];
  const periodFlat = useMemo(
    () =>
      chapters.flatMap((chapter, chapterIdx) =>
        (chapter.periodRespList || []).map((period) => ({
          chapterIdx,
          chapterName: chapter.chapterName || `第 ${chapterIdx + 1} 章`,
          period
        }))
      ),
    [chapters]
  );

  const activePeriodIndex = periodFlat.findIndex((item) => String(item.period.id) === activePeriodId);
  const nextPeriod = activePeriodIndex >= 0 ? periodFlat[activePeriodIndex + 1]?.period || null : null;
  const activePeriod = activePeriodIndex >= 0 ? periodFlat[activePeriodIndex]?.period : null;
  const activePeriodChapterName = activePeriodIndex >= 0 ? periodFlat[activePeriodIndex]?.chapterName : '';
  const progressPercent = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
  const mentor = course?.lecturerResp;
  const mentorTags = splitKeywords(mentor?.tags || mentor?.expertiseAreas);
  const mentorLanguages = splitKeywords(mentor?.languages);

  const stopSyncTimer = () => {
    if (syncTimerRef.current) {
      window.clearInterval(syncTimerRef.current);
      syncTimerRef.current = null;
    }
  };

  const syncStudyProgress = (studyStatus = 1) => {
    if (!studyRes?.studyId || !studyRes.resourceId || !videoRef.current) return;
    const currentDuration = Math.floor(videoRef.current.currentTime || 0);
    const progress = duration > 0 ? Math.floor((currentDuration / duration) * 100) : 0;
    void api.studyProgress({
      studyId: studyRes.studyId,
      resourceId: studyRes.resourceId,
      currentDuration,
      progress,
      studyStatus
    });
  };

  const startSyncTimer = () => {
    stopSyncTimer();
    syncTimerRef.current = window.setInterval(() => syncStudyProgress(1), 5000);
  };

  const destroyHls = () => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  };

  const attachVideoSource = (url: string, lastPosition = 0) => {
    const video = videoRef.current;
    if (!video || !url) return;
    destroyHls();
    if (Hls.isSupported() && url.includes('.m3u8')) {
      const hls = new Hls({
        autoStartLoad: false,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        backBufferLength: 30,
        lowLatencyMode: true
      });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (lastPosition > 0) {
          video.currentTime = lastPosition;
        }
        void video.play().catch(() => setIsPlaying(false));
      });
      hlsRef.current = hls;
      return;
    }
    video.src = url;
    video.load();
    if (lastPosition > 0) {
      video.currentTime = lastPosition;
    }
    void video.play().catch(() => setIsPlaying(false));
  };

  const handleStudy = async (period: CoursePeriod) => {
    if (!courseId || !period?.id || studyLoading) return;
    if (String(period.id) === activePeriodId) return;

    setStudyLoading(true);
    setError('');
    setShowNextMask(false);
    setIsPlaying(false);
    stopSyncTimer();
    if (videoRef.current) {
      videoRef.current.pause();
    }
    destroyHls();

    try {
      const chapterIdx = chapters.findIndex((chapter) =>
        (chapter.periodRespList || []).some((item) => String(item.id) === String(period.id))
      );
      if (chapterIdx >= 0) {
        setActiveChapterIdx(chapterIdx);
      }
      const sign = await api.studySign({ periodId: period.id, courseId });
      setStudyRes(sign);
      setActivePeriodId(String(sign.periodId ?? period.id));
      maxTimeWatchedRef.current = Number(sign.currentDuration || 0);

      const type = Number(sign.resourceType || 1);
      if (type <= 2) {
        setActiveType('video');
        setDocUrl('');
        const playUrl = toPlayUrl(sign);
        setMediaUrl(playUrl);
        attachVideoSource(playUrl, Number(sign.currentDuration || 0));
      } else if (type === 4) {
        setActiveType('image');
        setDocUrl('');
        setMediaUrl(sign.resourceUrl || '');
      } else {
        setActiveType('doc');
        setMediaUrl('');
        setDocUrl(sign.resourceUrl || '');
      }
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : '資源載入失敗，請稍後再試');
    } finally {
      setStudyLoading(false);
    }
  };

  useEffect(() => {
    if (!getToken()) {
      const redirect = encodeURIComponent(`/course/${courseId}/study${window.location.search}`);
      navigate(`/login?redirect=${redirect}`, { replace: true });
      return;
    }

    if (!courseId || !/^\d+$/.test(courseId)) {
      setError('課程參數無效');
      setLoading(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError('');
      try {
        const [detail, commentPage] = await Promise.all([
          api.getCourseDetail(courseId, true),
          api.getCourseComments(courseId, 1, 20)
        ]);
        if (cancelled) return;
        setCourse(detail);
        setComments(
          (commentPage.list || []).map((item) => ({
            id: item.id,
            commentText: item.commentText,
            nickname: item.usersVO?.nickname || '學員'
          }))
        );

        const queryPeriodId = searchParams.get('periodId');
        const firstPeriod = detail.chapterRespList?.[0]?.periodRespList?.[0];
        const targetPeriod =
          (detail.chapterRespList || [])
            .flatMap((chapter) => chapter.periodRespList || [])
            .find((period) => String(period.id) === String(queryPeriodId || '')) || firstPeriod;
        if (targetPeriod) {
          const targetChapterIdx = (detail.chapterRespList || []).findIndex((chapter) =>
            (chapter.periodRespList || []).some((period) => String(period.id) === String(targetPeriod.id))
          );
          setActiveChapterIdx(targetChapterIdx >= 0 ? targetChapterIdx : 0);
          await handleStudy(targetPeriod);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError instanceof Error ? requestError.message : '課程載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      stopSyncTimer();
      destroyHls();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const handlePlay = () => {
    setIsPlaying(true);
    if (hlsRef.current) {
      hlsRef.current.startLoad();
    }
    startSyncTimer();
  };

  const handlePause = () => {
    setIsPlaying(false);
    if (hlsRef.current) {
      hlsRef.current.stopLoad();
    }
    stopSyncTimer();
    syncStudyProgress(1);
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    setCurrentTime(video.currentTime || 0);
    setDuration(video.duration || 0);
    maxTimeWatchedRef.current = Math.max(maxTimeWatchedRef.current, video.currentTime || 0);
    if (video.buffered.length > 0 && Number.isFinite(video.duration) && video.duration > 0) {
      setBufferPercent((video.buffered.end(video.buffered.length - 1) / video.duration) * 100);
    }
  };

  const handleSeek = (event: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video || duration <= 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    const target = Math.max(0, Math.min(duration, ratio * duration));
    if (target <= maxTimeWatchedRef.current + 2) {
      video.currentTime = target;
    } else {
      setError('請先完成之前的學習內容');
    }
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  };

  const handleComplete = () => {
    syncStudyProgress(1);
    stopSyncTimer();
    setShowNextMask(true);
  };

  const handleRestart = () => {
    const video = videoRef.current;
    if (!video) return;
    maxTimeWatchedRef.current = 0;
    setShowNextMask(false);
    video.currentTime = 0;
    void video.play();
  };

  const handlePlaybackRate = (rate: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = rate;
  };

  const handleFullscreen = () => {
    const el = playerWrapRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      void el.requestFullscreen();
    } else {
      void document.exitFullscreen();
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#dbeafe_0%,_#f8fafc_35%,_#eef2ff_100%)]">
      <header className="sticky top-0 z-30 h-16 border-b border-white/80 bg-white/75 backdrop-blur-xl">
        <div className="max-w-[1800px] mx-auto h-full px-4 sm:px-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate(`/course/${courseId}`)}
              className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div className="min-w-0">
              <p className="text-[11px] text-slate-400">課程學習中心</p>
              <p className="text-sm font-semibold text-slate-800 truncate">{course?.courseName || '載入中...'}</p>
            </div>
          </div>
          <Link to="/account?tab=courses" className="text-xs font-semibold text-sky-700 hover:text-sky-800">
            返回我的課程
          </Link>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 py-4 sm:py-5">
        {error ? (
          <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-2 text-sm">{error}</div>
        ) : null}
        {loading ? (
          <div className="h-[65vh] rounded-3xl bg-white/80 border border-white flex items-center justify-center text-slate-500">
            正在載入課程...
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)_360px] gap-4">
            <aside className="rounded-3xl border border-white/90 bg-white/75 backdrop-blur-xl shadow-[0_18px_40px_-24px_rgba(15,23,42,0.45)] overflow-hidden h-fit">
              <div className="px-4 py-4 border-b border-slate-100">
                <p className="text-xs uppercase tracking-[0.16em] font-semibold text-cyan-700">目錄</p>
                <p className="text-xs text-slate-500 mt-1">共 {chapters.length} 章節</p>
              </div>
              <div className="max-h-[68vh] overflow-y-auto px-3 py-3 space-y-2">
                {chapters.map((chapter: CourseChapter, chapterIdx) => {
                  const isOpen = activeChapterIdx === chapterIdx;
                  return (
                    <div key={String(chapter.id)} className="rounded-2xl border border-slate-100 bg-white/80">
                      <button
                        onClick={() => setActiveChapterIdx(chapterIdx)}
                        className="w-full px-3 py-2.5 flex items-center justify-between text-left"
                      >
                        <div className="min-w-0">
                          <p className="text-[11px] text-slate-400">第 {chapterIdx + 1} 章</p>
                          <p className="text-sm font-semibold text-slate-800 truncate">{chapter.chapterName || '未命名章節'}</p>
                        </div>
                        <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-slate-400`} />
                      </button>
                      {isOpen ? (
                        <div className="pb-2">
                          {(chapter.periodRespList || []).map((period) => {
                            const active = String(period.id) === activePeriodId;
                            return (
                              <button
                                key={String(period.id)}
                                onClick={() => void handleStudy(period)}
                                className={`w-full px-3 py-2 flex items-start gap-2.5 text-left transition ${
                                  active ? 'bg-emerald-50' : 'hover:bg-slate-50'
                                }`}
                              >
                                <i className={`${active ? 'ri-play-circle-fill text-emerald-600' : 'ri-play-circle-line text-slate-400'} mt-0.5`} />
                                <div className="min-w-0 flex-1">
                                  <p className={`text-xs truncate ${active ? 'text-emerald-700 font-semibold' : 'text-slate-600'}`}>
                                    {period.periodName || '未命名課時'}
                                  </p>
                                  <div className="mt-0.5 flex items-center gap-2 text-[10px] text-slate-400">
                                    {Number(period.isFree || 0) === 1 ? <span className="rounded bg-emerald-100 text-emerald-700 px-1">試看</span> : null}
                                    {Number(period.periodProgress || 0) > 0 ? <span>已學 {period.periodProgress}%</span> : null}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </aside>

            <section className="rounded-3xl border border-white/90 bg-white/78 backdrop-blur-xl shadow-[0_24px_55px_-26px_rgba(15,23,42,0.4)] overflow-hidden">
              <div className="p-3 sm:p-4 border-b border-slate-100 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs text-slate-400 truncate">{activePeriodChapterName || '請選擇課時'}</p>
                  <p className="text-sm font-semibold text-slate-800 truncate">{activePeriod?.periodName || '尚未選擇課時'}</p>
                </div>
                <div className="text-xs text-slate-500">{studyLoading ? '載入資源中...' : isPlaying ? '播放中' : '已暫停'}</div>
              </div>

              <div ref={playerWrapRef} className="relative bg-[#020617] aspect-video">
                {activeType === 'video' ? (
                  <>
                    <video
                      ref={videoRef}
                      className="w-full h-full object-contain"
                      onPlay={handlePlay}
                      onPause={handlePause}
                      onTimeUpdate={handleTimeUpdate}
                      onEnded={handleComplete}
                      playsInline
                    />
                    {!isPlaying ? (
                      <button
                        onClick={togglePlay}
                        className="absolute inset-0 m-auto w-16 h-16 rounded-full bg-white/20 backdrop-blur text-white hover:bg-cyan-500/80 flex items-center justify-center"
                      >
                        <i className="ri-play-fill text-3xl ml-1" />
                      </button>
                    ) : null}
                    <div className="absolute left-0 right-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                      <div onClick={handleSeek} className="h-4 flex items-center cursor-pointer">
                        <div className="w-full h-1.5 bg-white/25 rounded-full overflow-hidden relative">
                          <div className="h-full bg-white/25" style={{ width: `${Math.min(100, Math.max(0, bufferPercent))}%` }} />
                          <div className="h-full bg-cyan-400 absolute left-0 top-0" style={{ width: `${Math.min(100, Math.max(0, progressPercent))}%` }} />
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs text-white/90">
                        <div className="flex items-center gap-3">
                          <button onClick={togglePlay} className="hover:text-cyan-300">
                            <i className={`text-lg ${isPlaying ? 'ri-pause-line' : 'ri-play-fill'}`} />
                          </button>
                          <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <select onChange={(e) => handlePlaybackRate(Number(e.target.value))} className="bg-white/15 border border-white/30 rounded px-1.5 py-0.5">
                            <option value={0.75}>0.75x</option>
                            <option value={1}>1x</option>
                            <option value={1.25}>1.25x</option>
                            <option value={1.5}>1.5x</option>
                            <option value={2}>2x</option>
                          </select>
                          <button onClick={handleFullscreen} className="hover:text-cyan-300">
                            <i className="ri-fullscreen-line text-lg" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}

                {activeType === 'image' ? <img src={mediaUrl} alt={activePeriod?.periodName || '課時圖片'} className="w-full h-full object-contain" /> : null}
                {activeType === 'doc' ? <iframe src={docUrl} title={activePeriod?.periodName || '課時文檔'} className="w-full h-full bg-white" /> : null}
                {!activeType ? <div className="w-full h-full flex items-center justify-center text-slate-300">請選擇課時開始學習</div> : null}

                {showNextMask ? (
                  <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                    <div className="text-center px-6">
                      <p className="text-sm text-white/70">本節學習完成</p>
                      <h3 className="mt-2 text-xl font-bold text-white">{nextPeriod?.periodName || '恭喜，課程已學完'}</h3>
                      <div className="mt-5 flex items-center justify-center gap-3">
                        {nextPeriod ? (
                          <button onClick={() => void handleStudy(nextPeriod)} className="px-4 py-2 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600">
                            下一節
                          </button>
                        ) : null}
                        <button onClick={handleRestart} className="px-4 py-2 rounded-full border border-white/30 text-white text-sm hover:bg-white/10">
                          重學本節
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </section>

            <aside className="rounded-3xl border border-white/90 bg-white/75 backdrop-blur-xl shadow-[0_18px_40px_-24px_rgba(15,23,42,0.45)] overflow-hidden h-fit">
              <div className="grid grid-cols-2 border-b border-slate-100">
                <button onClick={() => setRightTab('info')} className={`py-3 text-sm font-semibold ${rightTab === 'info' ? 'text-cyan-700 bg-cyan-50' : 'text-slate-500'}`}>課程簡介</button>
                <button onClick={() => setRightTab('comment')} className={`py-3 text-sm font-semibold ${rightTab === 'comment' ? 'text-cyan-700 bg-cyan-50' : 'text-slate-500'}`}>討論區</button>
              </div>
              <div className="p-4 max-h-[68vh] overflow-y-auto">
                {rightTab === 'info' ? (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800">關於課程</h4>
                    <div className="mt-2 text-sm leading-6 text-slate-600 [&>p]:mb-2 [&>ul]:list-disc [&>ul]:pl-5 [&>ol]:list-decimal [&>ol]:pl-5 [&>h1]:text-lg [&>h1]:font-bold [&>h2]:text-base [&>h2]:font-bold [&>h3]:text-sm [&>h3]:font-semibold [&>blockquote]:border-l-4 [&>blockquote]:border-slate-200 [&>blockquote]:pl-3 [&>blockquote]:text-slate-500 [&>a]:text-cyan-700 [&>a]:underline">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                        {course?.introduce || '講師暫未添加課程簡介。'}
                      </ReactMarkdown>
                    </div>
                    <div className="mt-4 space-y-2 text-xs text-slate-500">
                      <p className="flex items-center gap-2"><i className="ri-hd-line text-cyan-600" /> 高清影片</p>
                      <p className="flex items-center gap-2"><i className="ri-smartphone-line text-cyan-600" /> 支援多端學習</p>
                      <p className="flex items-center gap-2"><i className="ri-save-line text-cyan-600" /> 進度自動保存</p>
                    </div>

                    {mentor ? (
                      <div className="mt-5 rounded-2xl border border-slate-100 bg-slate-50/70 p-3">
                        <h5 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">導師資訊</h5>
                        <div className="mt-3 flex items-start gap-3">
                          <Avatar
                            src={mentor.lecturerHead}
                            alt={mentor.lecturerName || '導師'}
                            className="w-12 h-12 rounded-xl flex-shrink-0 bg-slate-200"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-800 truncate">{mentor.lecturerName || '官方導師'}</p>
                            <p className="text-xs text-slate-500 mt-0.5 truncate">{mentor.lecturerPosition || '專業導師'}</p>
                          </div>
                        </div>
                        {mentorTags.length ? (
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {mentorTags.slice(0, 6).map((tag) => (
                              <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-100">
                                {tag}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        <div className="mt-3 space-y-1.5 text-[11px] text-slate-500">
                          {mentorLanguages.length ? <p>語言：{mentorLanguages.join(' / ')}</p> : null}
                          {mentor.timezone ? <p>時區：{mentor.timezone}</p> : null}
                        </div>
                        {mentor.id ? (
                          <Link
                            to={`/mentor/${String(mentor.id)}`}
                            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-700 hover:text-cyan-800"
                          >
                            查看導師頁面
                            <i className="ri-arrow-right-line" />
                          </Link>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {comments.length === 0 ? (
                      <p className="text-sm text-slate-400">暫無討論內容</p>
                    ) : (
                      comments.map((item) => (
                        <article key={String(item.id)} className="rounded-2xl border border-slate-100 bg-slate-50/70 p-3">
                          <p className="text-xs font-semibold text-slate-700">{item.nickname}</p>
                          <p className="mt-1 text-sm text-slate-600 leading-6">{item.commentText}</p>
                        </article>
                      ))
                    )}
                  </div>
                )}
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
};

export default CourseStudyPage;
